import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, forwardRef, isValidElement, useState } from 'react';
import { cn } from './cn.js';
const SIZE_CLASSES = {
    sm: 'size-8 text-2xs',
    default: 'size-10 text-sm',
    lg: 'size-14 text-lg',
};
/** First+last initial (or first two letters of a single token). */
function deriveInitials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
/**
 * Circular avatar. Shows the image when it loads; otherwise a token-tinted
 * monogram. The initials fallback also covers a broken/missing `src` (external
 * URLs fail — the trust boundary here is the image loading, not the markup).
 */
export const Avatar = forwardRef(({ className, src, name, size = 'default', initials, ...props }, ref) => {
    const [failed, setFailed] = useState(false);
    const showImage = src && !failed;
    const label = initials ?? deriveInitials(name);
    return (_jsx("span", { ref: ref, className: cn('relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ' +
            'bg-muted font-medium text-secondary-foreground select-none', SIZE_CLASSES[size], className), ...props, children: showImage ? (_jsx("img", { src: src, alt: name, className: "size-full object-cover outline outline-1 outline-[oklch(0_0_0_/_0.1)] dark:outline-[oklch(1_0_0_/_0.1)]", onError: () => setFailed(true) })) : (_jsx("span", { "aria-hidden": initials ? undefined : 'true', children: label })) }));
});
Avatar.displayName = 'Avatar';
/**
 * Overlapping avatar stack (crew on a job, reviewers on an estimate). Each face
 * gets a card-colored ring so the overlap reads cleanly on any surface; anything
 * past `max` collapses into a trailing "+N" bubble.
 */
export const AvatarGroup = forwardRef(({ className, max, size = 'default', children, ...props }, ref) => {
    const items = Children.toArray(children).filter(isValidElement);
    const visible = max != null && items.length > max ? items.slice(0, max) : items;
    const overflow = items.length - visible.length;
    return (_jsxs("div", { ref: ref, className: cn('flex items-center [&>*]:-ml-2 [&>*]:ring-2 [&>*]:ring-card first:[&>*]:ml-0', className), ...props, children: [visible, overflow > 0 && (_jsxs("span", { className: cn('relative inline-flex shrink-0 items-center justify-center rounded-full ' +
                    'bg-muted font-medium text-secondary-foreground select-none', SIZE_CLASSES[size]), children: ["+", overflow] }))] }));
});
AvatarGroup.displayName = 'AvatarGroup';
